import classNames from "classnames";
import React from "react";
import { Translate } from "react-localize-redux";

import { Shift } from "../../types";

interface ShiftProps {
  activeLanguage?: string;
  shifts: Shift[] | Record<string, Shift[]>;
}

// Map language codes correctly
const localeMap: Record<string, string> = {
  en: "en-US",
  ee: "et-EE",
  fi: "fi-FI",
};

const formatDate = (dateString: string, locale: string = "fi-FI"): string => {
  const date = new Date(dateString);

  const formatter = new Intl.DateTimeFormat(locale, {
    weekday: "short", // e.g., 'pe' for perjantai
    day: "numeric",
    month: "numeric",
  });

  const parts = formatter.formatToParts(date);

  const weekday = parts.find((part) => part.type === "weekday")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  const month = parts.find((part) => part.type === "month")?.value;

  if (!weekday || !day || !month) {
    return dateString;
  }

  // Note: We capitalize the weekday and add a period after the month.
  const capitalizedWeekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);

  return `${capitalizedWeekday} ${day}.${month}.`;
};

const isToday = (dateString: string): boolean => {
  const today = new Date();
  const shiftDate = new Date(dateString);

  return (
    shiftDate.getFullYear() === today.getFullYear() &&
    shiftDate.getMonth() === today.getMonth() &&
    shiftDate.getDate() === today.getDate()
  );
};

const Shifts: React.FC<ShiftProps> = ({ shifts, activeLanguage }) => {
  const componentClass = classNames("c-shifts");
  // Normalize input: API may return an object keyed by date instead of an array
  const input = shifts as any;
  const normalizedShifts: Shift[] = Array.isArray(input)
    ? input
    : Object.values(input || {}).flat();

  // If shifts is empty, we don't need to render the calendar
  if (!normalizedShifts || !normalizedShifts.length) {
    return (
      <div className={componentClass}>
        <p className="c-shifts__no-shifts">
          <Translate id="barber.noShifts" />
        </p>
      </div>
    );
  }

  // Return shifts list with date, name and city
  return (
    <div className={componentClass}>
      <ul className="c-shifts__list">
        {normalizedShifts.map((shift) => (
          <li
            key={shift.id}
            className={`c-shifts__item ${
              isToday(shift.date) ? "is-current" : ""
            }`}
          >
            <span className="c-shifts__date">
              {formatDate(
                shift?.date,
                localeMap[activeLanguage ?? "fi"] || "fi-FI"
              )}
            </span>

            <span className="c-shifts__location">
              <span
                className={`wait-dot ${
                  isToday(shift.date) ? "wait-dot--green" : "wait-dot--gray"
                }`}
              ></span>
              {shift?.pob_name}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Shifts;
